<template>
  <v-main>
    <v-container fluid class="pa-6 settings-wrap">
      <div class="settings-header mb-6">
        <div class="text-overline text-medium-emphasis settings-eyebrow">Settings</div>
        <h1 class="text-h4 font-weight-bold settings-title mb-2">Audits</h1>
        <p class="text-body-1 text-medium-emphasis settings-subtitle mb-0">
          Manage the brands you're tracking — pause monitoring, archive when you're done, or bring one back anytime.
        </p>
      </div>

      <v-alert v-if="error" type="error" variant="tonal" class="mb-4" closable @click:close="error = null">
        {{ error }}
      </v-alert>

      <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
        <v-tabs v-model="statusTab" density="comfortable" color="primary" class="settings-tabs">
          <v-tab value="all">All ({{ counts.all }})</v-tab>
          <v-tab value="active">Active ({{ counts.active }})</v-tab>
          <v-tab value="paused">Paused ({{ counts.paused }})</v-tab>
          <v-tab value="archived">Archived ({{ counts.archived }})</v-tab>
        </v-tabs>

        <v-text-field
          v-model="search"
          class="settings-search"
          density="compact"
          variant="outlined"
          placeholder="Search brand, URL, product..."
          prepend-inner-icon="mdi-magnify"
          hide-details
          clearable
          style="max-width: 320px; min-width: 220px;"
        />
      </div>

      <v-card variant="outlined" class="settings-table-card">
        <div class="settings-table-wrap">
          <table class="settings-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th class="col-owner">Owner</th>
                <th style="min-width: 160px;">Folder</th>
                <th>Product</th>
                <th>Region</th>
                <th>Last visited</th>
                <th style="min-width: 140px;">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-10 text-medium-emphasis">Loading audits…</td>
              </tr>
              <tr v-else-if="tableRows.length === 0">
                <td colspan="7" class="text-center py-10 text-medium-emphasis">No audits match this filter.</td>
              </tr>
              <tr
                v-for="row in tableRows"
                :key="row.key"
                :class="row.type === 'header' ? 'folder-header-row' : 'folder-entity-row'"
                @click="row.type === 'header' ? toggleFolder(row.groupKey) : null"
              >
                <template v-if="row.type === 'header'">
                  <td colspan="7">
                    <div class="folder-header">
                      <v-icon size="20" class="mr-1">
                        {{ row.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                      </v-icon>
                      <v-icon size="18" class="mr-2" :class="{ 'folder-header-icon--system': !row.name }">
                        {{ row.name ? 'mdi-folder-outline' : 'mdi-folder-remove-outline' }}
                      </v-icon>
                      <span
                        class="folder-header-name"
                        :class="{ 'folder-header-name--system': !row.name }"
                      >
                        {{ row.name || 'Ungrouped' }}
                      </span>
                      <span class="folder-header-count text-medium-emphasis">{{ row.count }}</span>
                    </div>
                  </td>
                </template>
                <template v-else>
                  <td>
                    <div class="font-weight-bold brand-name">{{ row.entity.brand_name }}</div>
                    <a
                      v-if="row.entity.url"
                      :href="externalUrl(row.entity.url)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="brand-url text-body-2"
                    >
                      {{ displayUrl(row.entity.url) }}
                      <v-icon size="14" class="ml-1">mdi-open-in-new</v-icon>
                    </a>
                  </td>
                  <td class="col-owner">{{ ownerName(row.entity) }}</td>
                  <td @click.stop>
                    <v-select
                      :model-value="row.entity.folder || ''"
                      :items="folderSelectItems"
                      item-title="title"
                      item-value="value"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :disabled="actionLoadingId === row.entity.id"
                      @update:model-value="(val) => onFolderSelect(row.entity, val)"
                    >
                      <template #selection="{ item }">
                        <span :class="{ 'folder-system-label': item.raw.system }">
                          {{ item.title }}
                        </span>
                      </template>
                      <template #item="{ props, item }">
                        <v-list-item
                          v-bind="props"
                          :class="{ 'settings-folder-system-item': item.raw.system }"
                        />
                      </template>
                    </v-select>
                  </td>
                  <td>{{ row.entity.product_name || '—' }}</td>
                  <td>{{ row.entity.region || '—' }}</td>
                  <td>{{ formatDate(row.entity.last_visited_at) }}</td>
                  <td @click.stop>
                    <v-tooltip location="top" :disabled="canManage(row.entity)">
                      <template #activator="{ props: tooltipProps }">
                        <span v-bind="tooltipProps" class="d-inline-flex">
                          <v-menu location="bottom" :disabled="!canManage(row.entity)">
                            <template #activator="{ props: menuProps }">
                              <v-chip
                                v-bind="menuProps"
                                :color="statusChip(row.entity.status).color"
                                size="small"
                                variant="tonal"
                                class="status-chip"
                                :disabled="!canManage(row.entity) || actionLoadingId === row.entity.id"
                                append-icon="mdi-chevron-down"
                              >
                                <span class="status-dot mr-2" :class="`status-dot--${row.entity.status}`"></span>
                                {{ statusChip(row.entity.status).text }}
                              </v-chip>
                            </template>
                            <v-list density="compact" min-width="140">
                              <v-list-item
                                v-for="action in statusActions(row.entity)"
                                :key="action.key"
                                :title="action.label"
                                @click="runStatusAction(row.entity, action.key)"
                              />
                            </v-list>
                          </v-menu>
                        </span>
                      </template>
                      <span>Only owners can update the Status</span>
                    </v-tooltip>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <v-dialog v-model="newFolderDialog" max-width="420">
        <v-card>
          <v-card-title>New folder</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="newFolderName"
              label="Folder name"
              placeholder="e.g. ADP"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              autofocus
              @keyup.enter="confirmNewFolder"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="closeNewFolderDialog">Cancel</v-btn>
            <v-btn color="primary" :disabled="!newFolderName.trim()" @click="confirmNewFolder">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import { mapGetters } from "vuex";
import "@/store/index.js";

const NEW_FOLDER_VALUE = "__new_folder__";
const CLEAR_FOLDER_VALUE = "";
const UNGROUPED_KEY = "__ungrouped__";

export default {
  name: "Settings",
  data() {
    return {
      entities: [],
      loading: false,
      error: null,
      search: "",
      statusTab: "all",
      actionLoadingId: null,
      newFolderDialog: false,
      newFolderName: "",
      newFolderEntityId: null,
      collapsedFolders: {},
    };
  },
  computed: {
    ...mapGetters(["getUserID"]),
    currentUserId() {
      return this.getUserID;
    },
    counts() {
      const all = this.entities.length;
      let active = 0;
      let paused = 0;
      let archived = 0;
      this.entities.forEach((e) => {
        if (e.status === "archived") archived += 1;
        else if (e.status === "paused") paused += 1;
        else active += 1;
      });
      return { all, active, paused, archived };
    },
    folderOptions() {
      const names = new Set();
      this.entities.forEach((e) => {
        if (e.folder) names.add(e.folder);
      });
      return Array.from(names).sort((a, b) => a.localeCompare(b));
    },
    folderSelectItems() {
      return [
        { title: "Ungrouped", value: CLEAR_FOLDER_VALUE, system: true },
        ...this.folderOptions.map((name) => ({ title: name, value: name })),
        { title: "+ New folder", value: NEW_FOLDER_VALUE },
      ];
    },
    visibleEntities() {
      const q = (this.search || "").toLowerCase().trim();
      return this.entities.filter((e) => {
        if (this.statusTab === "active" && e.status !== "active" && e.status !== "warning") return false;
        if (this.statusTab === "paused" && e.status !== "paused") return false;
        if (this.statusTab === "archived" && e.status !== "archived") return false;
        if (!q) return true;
        const brand = (e.brand_name || "").toLowerCase();
        const product = (e.product_name || "").toLowerCase();
        const url = (e.url || "").toLowerCase();
        const folder = (e.folder || "").toLowerCase();
        const owner = this.ownerName(e).toLowerCase();
        return brand.includes(q) || product.includes(q) || url.includes(q) || folder.includes(q) || owner.includes(q);
      });
    },
    folderGroups() {
      const byFolder = new Map();
      this.visibleEntities.forEach((entity) => {
        const name = (entity.folder || "").trim();
        const key = name || UNGROUPED_KEY;
        if (!byFolder.has(key)) {
          byFolder.set(key, { key, name, entities: [] });
        }
        byFolder.get(key).entities.push(entity);
      });

      const groups = Array.from(byFolder.values());
      groups.sort((a, b) => {
        if (a.key === UNGROUPED_KEY) return 1;
        if (b.key === UNGROUPED_KEY) return -1;
        return a.name.localeCompare(b.name);
      });
      return groups;
    },
    tableRows() {
      const rows = [];
      this.folderGroups.forEach((group) => {
        const expanded = !this.collapsedFolders[group.key];
        rows.push({
          type: "header",
          key: `header-${group.key}`,
          groupKey: group.key,
          name: group.name,
          count: group.entities.length,
          expanded,
        });
        if (expanded) {
          group.entities.forEach((entity) => {
            rows.push({
              type: "entity",
              key: `entity-${entity.id}`,
              groupKey: group.key,
              entity,
            });
          });
        }
      });
      return rows;
    },
  },
  async created() {
    await this.loadEntities();
  },
  methods: {
    toggleFolder(key) {
      this.collapsedFolders = {
        ...this.collapsedFolders,
        [key]: !this.collapsedFolders[key],
      };
    },
    async loadEntities() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.$store.dispatch("fetchReviewEntities", { include_archived: true });
        this.entities = res?.review_entities || [];
      } catch (err) {
        this.error = err?.error || "Failed to load audits";
        this.entities = [];
      } finally {
        this.loading = false;
      }
    },
    canManage(entity) {
      if (!this.currentUserId) return false;
      return !entity.creator_id || entity.creator_id === this.currentUserId;
    },
    ownerName(entity) {
      const first = (entity.creator_first_name || "").trim();
      if (!first) return "—";
      const initial = (entity.creator_last_initial || "").trim();
      return initial ? `${first} ${initial}.` : first;
    },
    statusChip(status) {
      switch (status) {
        case "warning":
          return { color: "warning", text: "Warning" };
        case "paused":
          return { color: "warning", text: "Paused" };
        case "archived":
          return { color: "grey", text: "Archived" };
        default:
          return { color: "primary", text: "Active" };
      }
    },
    statusActions(entity) {
      if (entity.status === "archived") {
        return [{ key: "unarchive", label: "Unarchive" }];
      }
      if (entity.status === "paused") {
        return [
          { key: "unpause", label: "Unpause" },
          { key: "archive", label: "Archive" },
        ];
      }
      return [
        { key: "pause", label: "Pause" },
        { key: "archive", label: "Archive" },
      ];
    },
    patchEntity(id, fields) {
      const idx = this.entities.findIndex((e) => e.id === id);
      if (idx === -1) return;
      this.entities.splice(idx, 1, { ...this.entities[idx], ...fields });
    },
    async refreshPicker() {
      await this.$store.dispatch("fetchReviewEntities");
    },
    async runStatusAction(entity, action) {
      if (!this.canManage(entity) || this.actionLoadingId) return;
      this.actionLoadingId = entity.id;
      this.error = null;
      try {
        let res;
        if (action === "pause") {
          res = await this.$store.dispatch("pauseReviewEntity", entity.id);
          this.patchEntity(entity.id, {
            status: res?.status || "paused",
            paused_at: new Date().toISOString(),
            pause_reason: "manual",
          });
        } else if (action === "unpause") {
          res = await this.$store.dispatch("unpauseReviewEntity", entity.id);
          this.patchEntity(entity.id, {
            status: res?.status || "active",
            paused_at: null,
            pause_reason: null,
            last_visited_at: new Date().toISOString(),
          });
        } else if (action === "archive") {
          res = await this.$store.dispatch("archiveReviewEntity", entity.id);
          this.patchEntity(entity.id, {
            status: res?.status || "archived",
            archived_at: res?.archived_at || new Date().toISOString(),
          });
        } else if (action === "unarchive") {
          res = await this.$store.dispatch("unarchiveReviewEntity", entity.id);
          this.patchEntity(entity.id, {
            status: res?.status || "active",
            archived_at: null,
          });
        }
        await this.refreshPicker();
      } catch (err) {
        this.error = err?.error || `Failed to ${action} audit`;
        await this.loadEntities();
      } finally {
        this.actionLoadingId = null;
      }
    },
    onFolderSelect(entity, value) {
      if (value === NEW_FOLDER_VALUE) {
        this.newFolderEntityId = entity.id;
        this.newFolderName = "";
        this.newFolderDialog = true;
        return;
      }
      this.saveFolder(entity, value === CLEAR_FOLDER_VALUE ? null : value);
    },
    closeNewFolderDialog() {
      this.newFolderDialog = false;
      this.newFolderEntityId = null;
      this.newFolderName = "";
    },
    async confirmNewFolder() {
      const name = this.newFolderName.trim();
      const entityId = this.newFolderEntityId;
      if (!name || !entityId) return;
      const entity = this.entities.find((e) => e.id === entityId);
      this.closeNewFolderDialog();
      if (entity) await this.saveFolder(entity, name);
    },
    async saveFolder(entity, folder) {
      if (this.actionLoadingId) return;
      const previous = entity.folder || null;
      const next = folder || null;
      if (previous === next) return;
      this.actionLoadingId = entity.id;
      this.error = null;
      this.patchEntity(entity.id, { folder: next });
      const destKey = next || UNGROUPED_KEY;
      if (this.collapsedFolders[destKey]) {
        this.collapsedFolders = { ...this.collapsedFolders, [destKey]: false };
      }
      try {
        const res = await this.$store.dispatch("updateReviewEntityFolder", {
          reviewEntityId: entity.id,
          folder: next,
        });
        this.patchEntity(entity.id, { folder: res?.folder ?? next });
      } catch (err) {
        this.patchEntity(entity.id, { folder: previous });
        this.error = err?.error || "Failed to update folder";
      } finally {
        this.actionLoadingId = null;
      }
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
    displayUrl(url) {
      return (url || "").replace(/^https?:\/\//i, "").replace(/\/$/, "");
    },
    externalUrl(url) {
      if (!url) return "#";
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    },
  },
};
</script>

<style scoped>
.settings-wrap {
  max-width: 1400px;
}

.settings-eyebrow {
  letter-spacing: 0.12em;
}

.settings-title {
  line-height: 1.2;
}

.settings-subtitle {
  max-width: 42rem;
}

.settings-table-card {
  overflow: hidden;
}

.settings-table-wrap {
  overflow-x: auto;
}

.settings-table {
  width: 100%;
  border-collapse: collapse;
}

.settings-table th,
.settings-table td {
  text-align: left;
  padding: 12px 16px;
  border-bottom: thin solid rgba(0, 0, 0, 0.08);
  vertical-align: middle;
}

.settings-table th {
  font-weight: 600;
  background: rgba(0, 0, 0, 0.03);
  white-space: nowrap;
  font-size: 0.8125rem;
}

.settings-table .col-owner {
  min-width: 200px;
  white-space: nowrap;
}

.folder-header-row {
  cursor: pointer;
  background: rgba(25, 118, 210, 0.06);
}

.folder-header-row:hover {
  background: rgba(25, 118, 210, 0.1);
}

.folder-header-row td {
  padding-top: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(25, 118, 210, 0.15);
}

.folder-header {
  display: flex;
  align-items: center;
  user-select: none;
}

.folder-header-name {
  font-weight: 700;
  font-size: 0.95rem;
}

.folder-header-name--system {
  color: rgba(0, 0, 0, 0.45);
  font-weight: 600;
  font-style: italic;
}

.folder-header-icon--system {
  color: rgba(0, 0, 0, 0.4);
}

.folder-system-label {
  color: rgba(0, 0, 0, 0.45);
  font-style: italic;
}

.folder-header-count {
  margin-left: 8px;
  font-size: 0.85rem;
}

.folder-entity-row td:first-child {
  padding-left: 28px;
}

.brand-name {
  white-space: normal;
  word-break: break-word;
}

.brand-url {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.brand-url:hover {
  text-decoration: underline;
}

.status-chip {
  cursor: pointer;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: currentColor;
}

.status-dot--active {
  background: rgb(var(--v-theme-primary));
}

.status-dot--warning {
  background: rgb(var(--v-theme-warning));
}

.status-dot--paused {
  background: #f9a825;
}

.status-dot--archived {
  background: #9e9e9e;
}
</style>

<style>
.settings-folder-system-item .v-list-item-title {
  color: rgba(0, 0, 0, 0.45);
  font-style: italic;
}
</style>
